import type { Notification } from "@/modules/notifications/types";
import { apiRequest, getApiBaseUrl } from "@/shared/lib/apiClient";
import { getAuthToken } from "@/modules/safecheck/auth";
import { workContextHeaders } from "@/modules/safecheck/workContext";

export type ApiNotification = {
  id: string;
  type: string;
  item_key: string;
  message: string;
  read: boolean;
  created_at: string | null;
};

export async function fetchNotifications(): Promise<{
  table_exists: boolean;
  total: number;
  notifications: ApiNotification[];
}> {
  return apiRequest("/notifications", { auth: true });
}

export async function markNotificationRead(id: string): Promise<void> {
  await apiRequest(`/notifications/${id}/read`, { method: "PATCH", auth: true });
}

export async function markAllNotificationsRead(): Promise<void> {
  await apiRequest("/notifications/read-all", { method: "PATCH", auth: true });
}

export async function deleteNotification(id: string): Promise<void> {
  await apiRequest(`/notifications/${id}`, { method: "DELETE", auth: true });
}

export function mapApiNotification(row: ApiNotification): Notification {
  return {
    id: row.id,
    type: (row.type as Notification["type"]) || "system",
    itemKey: (row.item_key as Notification["itemKey"]) || "systemBackupComplete",
    // Message écrit par le serveur : titre neutre, pas celui de la clé par défaut (« Sauvegarde terminée »).
    title: "SafeCheck",
    message: row.message,
    read: row.read,
    // Heure du serveur en UTC (« 2026-11-04 09:50:26 »).
    createdAt: row.created_at ? new Date(`${row.created_at.replace(" ", "T")}Z`).getTime() : Date.now(),
  };
}

export async function downloadAdminBackup(): Promise<void> {
  const token = getAuthToken();
  const response = await fetch(`${getApiBaseUrl()}/admin/backup`, {
    method: "POST",
    headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}), ...workContextHeaders() },
  });

  if (!response.ok) {
    const payload = await response.json().catch(() => null);
    const message =
      payload && typeof payload === "object" && "message" in payload
        ? String((payload as { message?: string }).message)
        : "Export impossible.";
    throw new Error(message);
  }

  const blob = await response.blob();
  const disposition = response.headers.get("content-disposition") ?? "";
  const match = disposition.match(/filename="?([^"]+)"?/i);
  const filename = match?.[1] ?? `safecheck-backup-${new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-")}.sql`;
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
}

export async function restoreAdminBackup(file: File): Promise<{ message: string; filename: string }> {
  const form = new FormData();
  form.append("backup_file", file);
  return apiRequest<{ message: string; filename: string; size?: number }>("/admin/backup/restore", {
    method: "POST",
    auth: true,
    body: form,
  });
}

export type AdminSystemLog = {
  id: number;
  user_id: number;
  user: string;
  action: string;
  target_id: number | null;
  target_type: string | null;
  details: string | null;
  created_at: string | null;
};

export async function fetchAdminLogs(limit = 200): Promise<{
  total: number;
  logs: AdminSystemLog[];
}> {
  return apiRequest(`/admin/logs?limit=${limit}`, { auth: true });
}

export type AdminCacheEntry = {
  id: string;
  key: string;
  size: string;
  created_at: string;
  expires_at: string;
  expired: boolean;
};

export type AdminCacheStatus = {
  driver: string;
  total_entries: number;
  valid_entries: number;
  expired_entries: number;
  total_size_kb: number;
  max_size_mb: number;
  usage_percent: number;
  entries: AdminCacheEntry[];
};

export async function fetchAdminCacheStatus(): Promise<AdminCacheStatus> {
  return apiRequest<AdminCacheStatus>("/admin/cache", { auth: true });
}

export async function clearAdminCache(): Promise<{ message: string; cleared: boolean }> {
  return apiRequest("/admin/cache/clear", { method: "POST", auth: true });
}
